import { LightningElement } from 'lwc';
import { ConfirmOptions, ConfirmStatus, confirmStore, selectStatus } from '~/modules/stores/confirmStore';
import { navigate, routerStore } from '~/modules/stores/routerStore';

export const DEFAULT_DEBOUNCE_TIMEOUT = 300;

export function scrollToTop() {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera

    // eslint-disable-next-line @lwc/lwc/no-document-query
    (document.querySelector('#focus-reset') as HTMLElement)?.focus();
}

export function scrollToElement(element: LightningElement, scrollTo?: string) {
    if (!scrollTo) {
        return;
    }
    // Calling setTimeout with a delay of 0 allows LWC to render the template before we query for the scroll target element.
    setTimeout(() => {
        const scrollIntoElem = element.template!.querySelector('[data-scroll-to="' + scrollTo + '"]');
        if (!scrollIntoElem) {
            return;
        }

        scrollIntoElem.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

        // Remove scroll to param to allow users to scroll to the same element again, also scrolling to an element during page load doesn't work correctly
        const { url, queryString } = routerStore.getState();
        const queryParams = new URLSearchParams(queryString);
        queryParams.delete('scrollTo');
        navigate(`${url}?${queryParams}`);
    }, 0);
}

// Returns the scroll position to the same position after the callback
export function handleScrollLock(callback: (...args: unknown[]) => void): void {
    // While pageYOffset is deprecated, a bug in Chrome means the window
    // alwyas returns 0 after the popover is dismissed. pageYOffset does not.
    const previousScrollPosition = window.pageYOffset;
    callback();
    setTimeout(() => {
        window.scrollTo({
            top: previousScrollPosition,
            left: 0,
        });
    });
}

export function confirmPrompt(title: string, message: string, options: ConfirmOptions) {
    // Open the confirmation prompt with the given message.
    confirmStore.open(title, message, options);

    let resolvePromise: undefined | ((value: boolean | PromiseLike<boolean>) => void);

    // Wait for the user to click confirm or cancel, and based on their action, resolve the promise with either true or false
    const statusPromise = new Promise<boolean>((resolve) => {
        resolvePromise = resolve;
    });

    let unsubscribe: undefined | (() => void) = confirmStore.subscribe((confirmStatus: string) => {
        switch (confirmStatus) {
            case ConfirmStatus.CONFIRMED: {
                unsubscribe?.();
                unsubscribe = undefined;
                resolvePromise?.(true);
                resolvePromise = undefined;
                break;
            }
            case ConfirmStatus.CANCELED: {
                unsubscribe?.();
                unsubscribe = undefined;
                resolvePromise?.(false);
                resolvePromise = undefined;
                break;
            }
            default: {
                /* noop */
            }
        }
    }, selectStatus);

    return statusPromise;
}

export function appendMetaTag(name: string, content: string) {
    const tag = document.createElement('meta');
    tag.name = name;
    tag.content = content;
    document.head.appendChild(tag);
}

export function removeMetaTag(name: string) {
    const element = document.head.querySelector(`meta[name='${name}']`);
    if (!element) {
        console.error(`Failed to find head element with name='${name}', cannot remove it.`);
        return;
    }
    document.head.removeChild(element);
}

export function timeoutPromise(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce<T extends (...args: any[]) => any>(
    context: ThisParameterType<T>,
    func: T,
    param: Parameters<T>,
    debounceTimer: number,
    timeout = DEFAULT_DEBOUNCE_TIMEOUT,
) {
    clearTimeout(debounceTimer);
    const debounceEvent = Number(
        setTimeout(() => {
            func.call(context, ...param);
        }, timeout),
    );
    return debounceEvent;
}
